/** Pure logic for the profile's Job chips: order, the "+N more" cap and the summary line. Kept out of React so it's easy to test. */

/** Chips shown while collapsed. The primary job is always among them because it sorts first. */
export const JOB_CHIP_CAP = 3;

export type JobChipInput = { id: string; displayName: string };

export type JobChip = JobChipInput & {
	isPrimary: boolean;
	/** `undefined` while the state is unknown (loading, failed, or not in the read yet). */
	isSilent: boolean | undefined;
};

export type JobChipsView = {
	visible: JobChip[];
	/** How many jobs the "+N more" chip hides. 0 when none are hidden. */
	hiddenCount: number;
	/** True when the list is long enough to collapse. */
	isCollapsible: boolean;
	totalCount: number;
	notifyingCount: number;
	/** Every job is known to be silent, so the employee hears about no open shifts. */
	allSilent: boolean;
	/** Show "N jobs · M notifying" only while chips are hidden, since the row can't show it then. */
	showCountLine: boolean;
};

/** Primary first, then by name. The id breaks ties so the order is stable. */
export const orderJobChips = (jobs: readonly JobChipInput[], primaryJobId: string | undefined, silentByJobId: ReadonlyMap<string, boolean>): JobChip[] =>
	jobs
		.map((job) => ({ ...job, isPrimary: job.id === primaryJobId, isSilent: silentByJobId.get(job.id) }))
		.sort((a, b) => {
			if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
			return a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id);
		});

/** Collapses only from five jobs up, since "+1 more" at four takes as much room as the chip it hides. */
export const getJobChipsView = (chips: readonly JobChip[], expanded: boolean): JobChipsView => {
	const isCollapsible = chips.length > JOB_CHIP_CAP + 1;
	const isCollapsed = isCollapsible && !expanded;
	const visible = isCollapsed ? chips.slice(0, JOB_CHIP_CAP) : [...chips];
	const hiddenCount = chips.length - visible.length;
	const notifyingCount = chips.filter((chip) => chip.isSilent === false).length;
	const allSilent = chips.length > 0 && chips.every((chip) => chip.isSilent === true);
	return {
		visible,
		hiddenCount,
		isCollapsible,
		totalCount: chips.length,
		notifyingCount,
		allSilent,
		showCountLine: isCollapsed && !allSilent,
	};
};
